import { useEffect, useRef } from 'react';
import { useForm } from 'react-final-form';
import { Box, Unstable_Grid2 as Grid } from '@mui/material';

import { Typography, ButtonItem } from '@occc/ui-design-system';
import {
  Checkboxes,
  CurrencyInputField,
  ToggleButtonGroupField,
  ProductGroupSelectField,
} from '@occc-app/design-system';
import { Award_Per_Counting_Strategy } from '@occc/graphql';
import config from '@occc/config';

import { PRODUCT_DISCOUNT_TYPE, ProductDiscountValues } from '../../../types';

type ProductDiscountFormContentProps = {
  values: ProductDiscountValues;
};

const awardTypes: ButtonItem[] = [
  { title: 'free', value: PRODUCT_DISCOUNT_TYPE.FREE },
  { title: 'discounted', value: PRODUCT_DISCOUNT_TYPE.DISCOUNTED },
  { title: 'override', value: PRODUCT_DISCOUNT_TYPE.OVERRIDE },
];

const discountTypes: ButtonItem[] = [
  {
    title: 'Single Item',
    value: Award_Per_Counting_Strategy.IndividualPurchase,
  },
  {
    title: 'All Items',
    value: Award_Per_Counting_Strategy.TotalPurchase,
  },
];

export const ProductDiscountFormContent = ({ values }: ProductDiscountFormContentProps) => {
  const form = useForm();
  const partialDiscountWasChangedRef = useRef(false);

  const isPromptAtPumpEnabled = values.promptAtPump.isEnabled;

  const isDiscounted = values.productDiscount?.type === PRODUCT_DISCOUNT_TYPE.DISCOUNTED;
  const isOverride = values.productDiscount?.type === PRODUCT_DISCOUNT_TYPE.OVERRIDE;
  const showAmountField = isDiscounted || isOverride;
  const amountFieldLabel = isOverride ? 'Maximum cost' : 'Dollar(s) off';
  const isPerProduct =
    values.productDiscount?.discountType === Award_Per_Counting_Strategy.TotalPurchase;
  const partialDiscount = values.productDiscount?.partialDiscount;

  useEffect(() => {
    if (!config.features.promptAtPumpEnabled) {
      return;
    }

    if (isPromptAtPumpEnabled && !partialDiscount) {
      form.change('productDiscount.partialDiscount', true);
      partialDiscountWasChangedRef.current = true;
    } else if (!isPromptAtPumpEnabled && partialDiscountWasChangedRef.current) {
      form.change('productDiscount.partialDiscount', false);
      partialDiscountWasChangedRef.current = false;
    }
  }, [isPromptAtPumpEnabled, form, partialDiscount]);

  useEffect(() => {
    if (values.productDiscount?.type !== PRODUCT_DISCOUNT_TYPE.DISCOUNTED) {
      form.change('productDiscount.allowCarryOver', false);
    }
  }, [values.productDiscount?.type, form]);

  return (
    <>
      <Grid container>
        <Grid xs={12}>
          <Typography variant='subtitle1' paragraph>
            Give members
          </Typography>
        </Grid>
        <Grid container columnSpacing={3} xs={12}>
          <Grid xs='auto'>
            <ToggleButtonGroupField name='productDiscount.type' buttons={awardTypes} fitToContent />
          </Grid>
          {showAmountField && (
            <Grid xs={3}>
              <CurrencyInputField
                name='productDiscount.amount'
                label={amountFieldLabel}
                fieldProps={{
                  parse: undefined,
                }}
                inputProps={{
                  'data-testid': 'product-discount-amount',
                }}
              />
            </Grid>
          )}
        </Grid>
      </Grid>

      <Grid container sx={{ pt: 3 }}>
        <Grid xs={12}>
          <Typography variant='subtitle1' paragraph>
            For the following:
          </Typography>
        </Grid>
        <Grid xs={12}>
          <ProductGroupSelectField
            data-testid='product-groups-input'
            inputProps={{
              margin: 'none',
            }}
            name='productDiscount.productGroups'
            label='Product groups'
          />
        </Grid>
      </Grid>

      {isDiscounted && (
        <Grid container mt={3} columnSpacing={3}>
          <Grid>
            <Typography variant='subtitle1' paragraph>
              This discount is
            </Typography>

            <ToggleButtonGroupField
              name='productDiscount.discountType'
              buttons={discountTypes}
              fitToContent
            />
          </Grid>
          {isPerProduct && (
            <Grid alignItems='flex-end' display='flex'>
              <Box height={56} display='flex' alignItems='center'>
                <Checkboxes
                  name='productDiscount.partialDiscount'
                  data={{
                    label: 'Partial Discount Allowed',
                    value: false,
                    disabled: isPromptAtPumpEnabled,
                  }}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      )}
    </>
  );
};
